/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  emptyOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
  sortByIgnoreCase,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { MasterZoneDetailsDto } from './master-zone-details.dto';
import { MasterZoneDetailsMapper } from './master-zone-details.mapper';
import { MasterZoneListItemDto } from './master-zone-list-item.dto';
import { MasterZoneListItemMapper } from './master-zone-list-item.mapper';

@Injectable()
export class MasterZoneRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  create(masterzone: MasterZoneDetailsDto): Observable<MasterZoneDetailsDto> {
    return this.httpPost(`${this.bp}/master-zones`, masterzone, optimisticLockingOf(MasterZoneDetailsMapper));
  }

  update(masterzone: MasterZoneDetailsDto): Observable<MasterZoneDetailsDto> {
    return this.httpPut(`${this.bp}/master-zones/${masterzone.id}`, masterzone, optimisticLockingOf(MasterZoneDetailsMapper));
  }

  get(id: ID): Observable<MasterZoneDetailsDto> {
    return this.httpGet(`${this.bp}/master-zones/${id}`, optimisticLockingOf(MasterZoneDetailsMapper));
  }

  getInfo(id: ID): Observable<MasterZoneDetailsDto> {
    return this.httpGet(`${this.bp}/master-zones/${id}/view`, optimisticLockingOf(MasterZoneDetailsMapper));
  }

  deactivate(masterzone: MasterZoneListItemDto): Observable<MasterZoneListItemDto> {
    return this.httpPut(
      `${this.bp}/master-zones/${masterzone.id}/deactivate`,
      masterzone,
      emptyOf(optimisticLockingOf(MasterZoneListItemMapper)),
    );
  }

  validate(id: ID): Observable<any> {
    return this.httpGet(`${this.bp}/master-zones/${id}/validate`, identity());
  }

  findAll(): Observable<MasterZoneListItemDto[]> {
    return this.httpGet(`${this.bp}/master-zones`, arrayOf(MasterZoneListItemMapper)).pipe(map$(sortByIgnoreCase('name')));
  }

  findAllActive(): Observable<MasterZoneListItemDto[]> {
    return this.httpGet(`${this.bp}/master-zones`, arrayOf(MasterZoneListItemMapper)).pipe(
      map$((masterZones) => masterZones.filter((mz) => mz.active)));
  }

  findAllAsDataSource(): SlicedDataSource<MasterZoneListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/master-zones`, arrayOf(MasterZoneListItemMapper));
  }

  getAllForView(): SlicedDataSource<MasterZoneListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/master-zones/view`, arrayOf(MasterZoneListItemMapper));
  }
}
